import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { Check as CheckIcon, PlayCircle as PlayCircleIcon } from "@mui/icons-material";
import NoticeboardButton from "./NoticeboardButton";

interface EmptyStateProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  buttonText: string;
  onButtonClick: () => void;
  benefits?: string[];
  benefitsTitle?: string;
  benefitsShowCheckmarks?: boolean;
  howItWorks?: string[];
  howItWorksTitle?: string;
  buttonStartIcon?: React.ReactNode;
  onBenefitsTitleVideoClick?: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  buttonText,
  onButtonClick,
  benefits,
  benefitsTitle,
  benefitsShowCheckmarks = true,
  howItWorks,
  howItWorksTitle,
  buttonStartIcon,
  onBenefitsTitleVideoClick,
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 4, sm: 6, md: 8 },
        borderRadius: 3,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "background.paper",
      }}
    >
      {/* Main Content - Two Column Layout */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", lg: "row" },
          gap: 4,
          alignItems: { xs: "stretch", lg: "flex-start" },
        }}
      >
        {/* Left Column - Title, Description, and Button */}
        <Box sx={{ flex: 1 }}>
          {/* Header Section */}
          <Box sx={{ mb: 4, mt: -2 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                mb: 1,
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 400,
                  color: "text.primary",
                  fontSize: { xs: "1.5rem", sm: "1.75rem", md: "2rem" },
                }}
              >
                {title}
              </Typography>
            </Box>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                fontSize: { xs: "1rem", sm: "1.125rem" },
                lineHeight: 1.6,
              }}
            >
              {description}
            </Typography>
          </Box>

          {/* Action Card */}
          <Box
            sx={{
              bgcolor: "background.paper",
              borderRadius: 2,
              p: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {/* Benefits section - Inside the button box */}
            {benefits && benefits.length > 0 && (
              <Box sx={{ mb: 3 }}>
                {benefitsTitle && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 3 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 400,
                        color: "text.primary",
                      }}
                    >
                      {benefitsTitle}
                    </Typography>
                    {onBenefitsTitleVideoClick && (
                      <Box
                        onClick={onBenefitsTitleVideoClick}
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          color: "primary.main",
                          "&:hover": { color: "primary.dark" },
                          transition: "color 0.2s ease",
                        }}
                      >
                        <PlayCircleIcon sx={{ fontSize: 28 }} />
                      </Box>
                    )}
                  </Box>
                )}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {benefits.map((benefit, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      {benefitsShowCheckmarks && (
                        <CheckIcon
                          sx={{
                            color: "success.main",
                            fontSize: 20,
                          }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        sx={{
                          color: "text.primary",
                          fontWeight: 400,
                        }}
                      >
                        {benefit}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <NoticeboardButton
              variant="primary"
              onClick={onButtonClick}
              startIcon={buttonStartIcon}
              fullWidth
              sx={{
                py: 1.5,
                fontSize: "1rem",
                fontWeight: 400,
              }}
            >
              {buttonText}
            </NoticeboardButton>
          </Box>
        </Box>

        {/* Right Column - Illustration */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Box
              component="img"
              src={imageSrc}
              alt={imageAlt}
              loading="lazy"
              decoding="async"
              sx={{
                width: "100%",
                maxWidth: 500,
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>

          {/* How it works section - Below the image */}
          {howItWorks && howItWorks.length > 0 && (
            <Box sx={{ mt: -2.5 }}>
              {howItWorksTitle && (
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 400,
                    color: "text.primary",
                    mb: 2,
                  }}
                >
                  {howItWorksTitle}
                </Typography>
              )}
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {howItWorks.map((step, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                    }}
                  >
                    <CheckIcon
                      sx={{
                        color: "success.main",
                        fontSize: 20,
                      }}
                    />
                    <Typography
                      variant="body1"
                      sx={{
                        color: "text.primary",
                        fontWeight: 400,
                      }}
                    >
                      {step}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default EmptyState;
